import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MediaObserver, MediaChange } from '@angular/flex-layout';

import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';
import { UserService } from '../../services/user.service';
import { CompanyService } from '../../services/company.service';
import { NoteService } from '../../services/note.service';

import { CompanyInformation } from '../../models/company';
import { NoteListItem, NoteListGroupHeader, NoteListGroupFooter } from 'src/app/models/note';
import { NoteDetailTypes } from '../note-detail/note-detail.component';

@Component({
  selector: 'app-loan-details',
  templateUrl: './loan-details.component.html',
  styleUrls: ['./loan-details.component.scss']
})
export class LoanDetailsComponent implements OnInit, AfterViewInit {
  // Have to use the following ElementDisplayStyle variables and ngStyle to show/hide table elements
  // This because the table paginator will not attach correctly otherwise - this is a work around for a known issue
  private _hideElementDisplayStyle = 'none';
  private _showElementDisplayStyle = 'inline';
  private _showElementDisplayStyleIE = 'inline-block';

  private _handleError: HandleError;
  private _logging = environment.logging;
  private _appUrls = environment.appUrls;

  isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

  userFullName = '';
  participantIdString = '';
  participantId: number = null;
  notesCount = 0;
  notesList: (NoteListItem | NoteListGroupHeader | NoteListGroupFooter)[] = [];

  companyInformation: CompanyInformation;

  selectedNoteId: number = null;
  noteDetailTypes = NoteDetailTypes;

  showSpinner = false;

  // DataTable
  tableDisplayedColumns = ['noteId', 'noteStatus', 'noteDate', 'cpb', 'nextPaymentDueDate', 'pastDueAmount', 'totalAmountDue'];
  tableDataSource = new MatTableDataSource<NoteListItem | NoteListGroupHeader | NoteListGroupFooter>(this.notesList);
  tableDisplayStyle: string = this._hideElementDisplayStyle;
  tableDisplayStyleIE: string = this._hideElementDisplayStyle;
  notesFound = false;

  cardNoteList: (NoteListItem)[] = [];
  filteredNotesList = [...this.cardNoteList];
  selectedNoteStatus = '';
  uniqueNoteStatuses: string[] = [];

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _errorHandler: ErrorHandler,
    private _userService: UserService,
    private _companyService: CompanyService,
    private _noteService: NoteService,
    public media: MediaObserver
  ) {
    this._handleError = _errorHandler.createHandleError('LoanDetailsComponent');
    this.userFullName = this._userService.getFullName();
    this.participantId = this._userService.getParticipantId();
    this.participantIdString = this.participantId.toString();
    this.getData();
    this.scrollToTop();
  }

  ngOnInit() {
    this.notesFound = true;
    this.tableDisplayStyle = this.notesFound ? this._showElementDisplayStyle : this._hideElementDisplayStyle;
    this.tableDisplayStyleIE = this.notesFound ? this._showElementDisplayStyleIE : this._hideElementDisplayStyle;
  }

  ngAfterViewInit() {}

  getData(): void {
    this.showSpinner = true;
    this.tableDisplayStyle = this._hideElementDisplayStyle;
    this.tableDisplayStyleIE = this._hideElementDisplayStyle;

    this.companyInformation = this._companyService.getNewCompanyInformation();

    this._companyService.companyInformation.subscribe(
      response => (this.companyInformation = response),
      error => this._handleError('companyInformation.subscribe', error)
    );

    this.getNotesList(this.participantId);
  }

  getNotesList(participantId: number): void {
    this.showSpinner = true;
    this._noteService.getNoteList(participantId).subscribe(
      resultArray => {
        this.notesCount = resultArray.length;
        this.cardNoteList = resultArray;
        this.filteredNotesList = [...this.cardNoteList];
        this.uniqueNoteStatuses = [...new Set(this.cardNoteList.map(note => note.noteStatus))];

        this.notesList = this._noteService.addNoteListHeadersFooters(resultArray);
        if (this._logging.serviceCallTraceLog) {
          console.log('LoanDetailsComponent.getNotesList :: result.length = ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('LoanDetailsComponent.getNotesList :: result = ' + JSON.stringify(resultArray));
        }

        this.tableDataSource = new MatTableDataSource<NoteListItem | NoteListGroupHeader | NoteListGroupFooter>(this.notesList);
        this.notesFound = resultArray.length > 0;
        this.tableDisplayStyle = this.notesFound ? this._showElementDisplayStyle : this._hideElementDisplayStyle;
        this.tableDisplayStyleIE = this.notesFound ? this._showElementDisplayStyleIE : this._hideElementDisplayStyle;
        this.getDataComplete();
      },
      error => this._handleError('getNotesList', error)
    );
  }

  filterNotes() {

    this.selectedNoteId = null;

    if (this.selectedNoteStatus) {
      this.filteredNotesList = this.cardNoteList.filter(
        note => note.noteStatus === this.selectedNoteStatus
      );
    } else {
      this.filteredNotesList = [...this.cardNoteList];
    }
  }

  getDataComplete(): void {
    this.showSpinner = false;
  }

  isGroupHeader(index, item): boolean {
    return item.isGroupHeader;
  }

  isGroupFooter(index, item): boolean {
    return item.isGroupFooter;
  }

  selectNote(noteId: number) {
    if (this._userService.isLoggedIn()) {
      this.selectedNoteId = noteId;
      setTimeout(() => {
        const el = document.getElementById('noteDetails');
        el.scrollIntoView({ behavior: 'smooth' });
      });
    }
  }

  selectCardNote(noteId: number) {
    if (this._userService.isLoggedIn()) {
      this.selectedNoteId = noteId;
    }
  }

  scrollToTop() {
    const contentStart = document.getElementsByClassName('mat-sidenav-content');
    if (contentStart.length > 0) {
      contentStart[0].scrollTop = 0;
    }
  }

  getToolTip(element: string): string {
    let toolTip: string;
    switch (element.toUpperCase().trim()) {
      case 'NOTESTATUS': {
        toolTip = 'What does My Loan Status Mean?';
        break;
      }
      case 'NOTEID': {
        toolTip = 'To obtain a copy of your contract, please contact us via the “Contact Us” link at the top of the page,' +
                  ' or you can contact your lender for a copy.';
        break;
      }
      case 'CPB': {
        toolTip = 'This amount does not include pending disbursements';
        break;
      }
      default: {
        toolTip = 'No ToolTip message has been defined for ' + element;
        break;
      }
    }
    return toolTip;
  }

  openToolTip(element: string): void {
    switch (element.toUpperCase().trim()) {
      case 'NOTESTATUS': {
        if (this._logging.navigationTraceLog) {
          console.log('LoanDetailsComponent.openToolTip');
        }
        window.open(this.companyInformation.loanStatusDescriptionsURL, '_blank');
        break;
      }
      case 'NOTESTATUS': {
        if (this._logging.navigationTraceLog) {
          console.log('LoanDetailsComponent.openToolTip');
        }
        window.open('https://www.tuitionoptions.com/account_status/', '_blank');
        break;
      }
    }
  }
}
